<template>
  <div class="mt-3 billing-container">
    <billing-encounter-filters
      v-model="filter"
      :hasFilters="hasFilters"
      :loading="loadingMoreCharges"
      :showing="showing"
      ref="filterHeight"
    />

    <div class="mt-3 mb-0 w-100 d-flex flex-row justify-content-between">
      <div class="d-flex align-items-center" style="font-size: 19px">
        <div
          :class="
            billingsSelected.length > 0 ? 'hover-pointer hover-secondary' : ''
          "
          class="px-2 py-1"
          v-if="isPracticeSuiteActive || isSaisystemActive"
          @click="submitMultipleCharges"
        >
          <i
            class="fas fa-file-invoice"
            :class="
              billingsSelected.length == 0
                ? 'color-custom-dark-grey'
                : 'text-primary'
            "
            style="font-size: 15px"
          ></i>
          <span
            class="ml-2"
            :class="
              billingsSelected.length > 0
                ? 'text-primary'
                : 'color-custom-dark-grey'
            "
            style="font-size: 14px"
            >Submit for billing</span
          >
        </div>
        <div
          v-if="hasCurrentUserReportPermission('Billings')"
          class="border-left border-med-light-gray pl-1 ml-1"
          style="width: 1px; height: 19px"
        ></div>
        <div v-if="hasCurrentUserReportPermission('Billings')">
          <div @click="showReportRequestsModal = true">
            <div class="px-2 py-1 hover-pointer hover-secondary">
              <i
                class="fa fa-download text-primary"
                style="font-size: 15px"
              ></i>
              <span class="ml-2 text-primary" style="font-size: 14px"
                >Export</span
              >
            </div>
          </div>
        </div>
        <div
          v-if="currentUser && !currentUser.onlyAccessAssignedPatients"
          class="border-left border-med-light-gray pl-1 ml-1 mr-1"
          style="width: 1px; height: 19px"
        ></div>
        <vue-toggles
          v-if="currentUser && !currentUser.onlyAccessAssignedPatients"
          @click="changeAssignedToMEToggle"
          :value="assignedToMe"
          height="20"
          width="30"
          checkedBg="#34C759"
          uncheckedBg="#FF6161"
        />
        <small
          v-if="currentUser && !currentUser.onlyAccessAssignedPatients"
          class="ml-2"
          >Show My Patients only</small
        >
      </div>
      <div class="d-flex flex-row justify-content-end align-items-center">
        <top-paginator
          :total="listTotal"
          :page="filter.page"
          :perPage="filter.pageSize"
          @change-page="changePage"
        />
      </div>
    </div>
    <div
      class="clients-list"
      :class="hasFilters ? 'clients-list-filtered' : ''"
      :style="`--filter-height:` + filterHeight()"
    >
      <grid
        ref="grid"
        :groupFeature="false"
        :columns="columns"
        :store="gridConfig.store"
        :autoHeight="true"
        :data="items"
        :selectionMode="gridConfig.selectionMode"
        :columnLines="gridConfig.columnLines"
        :searchFeature="true"
        @storesort="listeners.storeSort"
        @selectionchange="listeners.selectionChange"
        @renderrows="onRenderRows"
        @renderrow="onRenderRows"
        @paint="listeners.paint"
      />
    </div>

    <billing-view-modal
      v-if="billingViewModal.show"
      v-model="billingViewModal.show"
      :billingId="billingViewModal.id"
      @onModify="onModifyEditBilling"
    />
    <billing-mass-submit-modal
      v-if="billingMassSubmitModal.show"
      v-model="billingMassSubmitModal.show"
      :billings="billingMassSubmitModal.billings"
      @onModify="onModifySubmitBillings"
    />
    <billing-charge-add-modal
      v-if="ubChargeModal.show"
      v-model="ubChargeModal.show"
      :encounterId="ubChargeModal.encounterId"
      @onModify="onModifySubmitUb04Charge"
    />
    <report-request-index
      v-if="showReportRequestsModal"
      v-model="showReportRequestsModal"
      :reportFilters="exportFilter"
      reportName="Billings"
    />
    <billing-document-invoice-add-edit-modal
      :id="newInvoiceModal.id"
      v-if="newInvoiceModal.show"
      v-model="newInvoiceModal.show"
      :showing="newInvoiceModal.show"
      :episodeOfCareId="newInvoiceModal.episodeOfCareId"
      :encounterIds="newInvoiceModal.encounterIds"
      @close="
        () => {
          newInvoiceModal.show = false;
          newInvoiceModal.id = null;
          this.fetchInitialData();
        }
      "
    />
    <billing-document-invoice-view-modal
      v-model="invoiceViewModal.show"
      :id="invoiceViewModal.id"
      :showing="invoiceViewModal.show"
      @edit="
        id => {
          this.newInvoiceModal.id = id;
          this.newInvoiceModal.show = true;
          this.invoiceViewModal.show = false;
        }
      "
      @close="
        () => {
          this.newInvoiceModal.id = null;
          this.invoiceViewModal.id = null;
          this.fetchInitialData();
        }
      "
      @payment="
        data => {
          this.addPaymentFromInvoiceView(data);
        }
      "
    />
    <payment-add-edit-modal
      v-if="newPaymentModal.show"
      v-model="newPaymentModal.show"
      :invoiceId="newPaymentModal.id"
      :patientId="newPaymentModal.patientId"
      :transactionType="'Payment'"
      :formTitle="`Add payment for ${newPaymentModal.patientName}`"
      @onHidden="
        () => {
          this.newPaymentModal.show = false;
          this.newPaymentModal.id = null;
          this.newPaymentModal.patientId = null;
          this.fetchInitialData();
        }
      "
      @success="
        () => {
          newPaymentModal.show = false;
          this.newPaymentModal.id = null;
          this.newPaymentModal.patientId = null;
          fetchInitialData();
        }
      "
    />
  </div>
</template>

<script>
import {
  formatDate,
  formatDateOnly,
  formatEndDateSql,
  formatStartDateSql
} from "../../../helpers/date";
import { lightenDarkenColor } from "../../../helpers/general";
import { validateEncountersForSubmit } from "../../../helpers/encounter";
import { mapActions, mapState, mapGetters } from "vuex";
import BillingViewModal from "./BillingViewModal.vue";
import BillingMassSubmitModal from "./BillingMassSubmitModal";
import Grid from "../../../third_party/grid/Grid.vue";
import TopPaginator from "../../controls/TopPaginator.vue";
import billingServer from "../../../server/billing";
import reportServer from "../../../server/report";
import BillingChargeAddModal from "./BillingUB04ChargeAddModal.vue";
import ReportRequestIndex from "../../report_request/ReportRequestIndex.vue";
import BillingEncounterFilters from "./BillingEncounterFilters";
import BillingDocumentInvoiceAddEditModal from "../billing_document/BillingDocumentInvoiceAddEditModal.vue";
import BillingDocumentInvoiceViewModal from "../billing_document/BillingDocumentInvoiceViewModal.vue";
import PaymentAddEditModal from "../billing_transactions/PaymentAddEditModal.vue";

export default {
  name: "Encounter",
  props: ["showing"],
  components: {
    Grid,
    TopPaginator,
    BillingViewModal,
    BillingMassSubmitModal,
    BillingChargeAddModal,
    BillingEncounterFilters,
    ReportRequestIndex,
    BillingDocumentInvoiceAddEditModal,
    BillingDocumentInvoiceViewModal,
    PaymentAddEditModal
  },
  data() {
    const self = this;
    return {
      filter: {
        submittedStatus: [
          {
            value: "NotSubmitted",
            label: "Not submitted"
          }
        ],
        documentationStatus: [],
        eocId: [],
        providerId: [],
        locations: [],
        services: [],
        appointmentStatus: [],
        diagnosisCodes: [],
        placeOfServices: [],
        dateFrom: null,
        dateTo: null,
        fee: {
          from: null,
          to: null
        },
        insuranceCompanies: [],
        insuranceFee: {
          from: null,
          to: null
        },
        client: {
          from: null,
          to: null
        },
        univoicedAmount: {
          from: null,
          to: null
        },
        searchPhrase: "",
        page: 0,
        pageSize: 50,
        orderBy: "dateAndTime",
        orderByDescending: true
      },
      clientSearchChanged: false,
      loadingMoreCharges: false,
      list: [],
      listTotal: 0,
      refetchBillings: true,
      gridConfig: {
        minHeight: "20em",
        selectionMode: {
          row: true,
          checkbox: true,
          rowCheckboxSelection: true,
          showCheckAll: true
        },
        columnLines: false,
        store: {
          sorters: [{ field: "dateAndTime", ascending: false }]
        }
      },
      billingViewModal: {
        show: false,
        id: null
      },
      billingMassSubmitModal: {
        show: false,
        billings: null
      },
      listeners: {
        selectionChange: selectionEvent => {
          self.billingsSelected = selectionEvent.selection.map(x => x.data);
        },
        storeSort: sortData => {
          if (sortData.sorters.length == 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            const field = sorter.field;
            self.filter.orderBy = field;
            self.filter.orderByDescending = !ascending;
            self.changePage(0);
          }
        },
        paint: paintEvent => {
          if (paintEvent.firstPaint) {
            window.bryntum.grid.EventHelper.on({
              element: paintEvent.source._element,
              delegate: "div.more-invoices",
              click: async event => {
                event.stopPropagation();
                const groupId = event.srcElement.dataset.id;
                self.showAllInvoices = {
                  ...self.showAllInvoices,
                  [groupId]: true
                };
              },
              capture: true,
              thisObj: this
            });
            window.bryntum.grid.EventHelper.on({
              element: paintEvent.source._element,
              delegate: "div.less-invoices",
              click: async event => {
                event.stopPropagation();
                const groupId = event.srcElement.dataset.id;
                self.showAllInvoices = {
                  ...self.showAllInvoices,
                  [groupId]: false
                };
              },
              capture: true,
              thisObj: this
            });
          }
        }
      },
      billingsSelected: [],
      ubChargeModal: {
        show: false,
        encounterId: null
      },
      showReportRequestsModal: false,
      showAllInvoices: {},
      newInvoiceModal: {
        show: false,
        id: null,
        episodeOfCareId: null,
        encounterIds: []
      },
      invoiceViewModal: {
        show: false,
        id: null
      },
      newPaymentModal: {
        show: false,
        id: null,
        patientId: null,
        patientName: null
      }
    };
  },
  methods: {
    ...mapActions("layout", [
      "showAlertSuccess",
      "showError",
      "show",
      "hide",
      "toggleAssignToMeBillingIndex"
    ]),
    createViewListener() {
      const elements = document.querySelectorAll("[id^='invoice_']");
      elements.forEach(element => {
        element.addEventListener("click", e => {
          this.onViewInvoice(e);
        });
      });
    },
    createAddListener() {
      const createElements = document.querySelectorAll(
        "[id^='createInvoice_']"
      );
      createElements.forEach(element => {
        element.addEventListener("click", e => {
          this.onAddInvoice(e);
        });
      });
    },
    async fetchInitialData() {
      this.fetchBillings(true);
    },
    fetchBillings(instantReload = false) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(
        async () => {
          let result = await billingServer.queryImproved({
            eocId: this.filter.eocId.map(s => s.value),
            userIds: this.filter.providerId.map(s => s.value),
            locationId: this.filter.locations.map(x => x.value),
            submittedStatus: this.filter.submittedStatus.map(s => s.value),
            documentationStatus: this.filter.documentationStatus.map(
              s => s.value
            ),
            dateFrom: formatStartDateSql(this.filter.dateFrom),
            dateTo: formatEndDateSql(this.filter.dateTo),
            searchPhrase: this.filter.searchPhrase,
            page: this.filter.page,
            pageSize: this.filter.pageSize,
            orderBy: this.filter.orderBy,
            orderByDescending: this.filter.orderByDescending,
            assignedToMe: this.assignedToMe,
            serviceIds: this.filter.services.map(service => service.value),
            appointmentStatusIds: this.filter.appointmentStatus.map(
              status => status.value
            ),
            diagnosisCodeIds: this.filter.diagnosisCodes.map(
              icdCode => icdCode.value
            ),
            placeOfServiceIds: this.filter.placeOfServices.map(
              pos => pos.value
            ),
            feeFrom: this.filter.fee.from || null,
            feeTo: this.filter.fee.to || null,
            insuranceCompanies: this.filter.insuranceCompanies.map(
              insuranceCompany => insuranceCompany.value
            ),
            insuranceFrom: this.filter.insuranceFee.from || null,
            insuranceTo: this.filter.insuranceFee.to || null,
            clientFrom: this.filter.client.from || null,
            clientTo: this.filter.client.to || null,
            uninvoicedAmountFrom: this.filter.univoicedAmount.from || null,
            uninvoicedAmountTo: this.filter.univoicedAmount.to || null
          });
          this.list = result.encounters;
          this.listTotal = result.totalCount;
        },
        instantReload ? 0 : 1000
      );
    },
    onRenderRows() {
      this.hookDropdownButtons();
      this.createViewListener();
      this.createAddListener();
    },
    filterHeight() {
      return (
        (this.$refs.filterHeight
          ? this.$refs.filterHeight.$el.clientHeight
          : 0) + "px"
      );
    },
    submitMultipleCharges() {
      if (this.billingsSelected.length == 0) {
        return;
      }
      this.billingMassSubmitModal.show = true;
      this.billingMassSubmitModal.billings = this.billingsSelected;
    },
    changePage(page) {
      this.filter.page = page;
      this.fetchBillings(true);
      this.scrollGridToTop();
    },
    scrollGridToTop() {
      const el = document.getElementsByClassName("b-vertical-overflow");
      Array.from(el).forEach(e => {
        if (e.scrollTop > 0) e.scrollTop = 0;
      });
    },
    onModifyEditBilling() {
      this.billingViewModal.show = false;
      this.fetchBillings(true);
    },
    onModifySubmitBillings() {
      this.billingMassSubmitModal.show = false;
      this.fetchBillings(true);
    },
    onViewInvoice(event) {
      const [, result] = event.currentTarget.id.split("_");
      this.invoiceViewModal.id = result;
      this.invoiceViewModal.show = true;
    },
    onAddInvoice(event) {
      const [, encounter, eoc] = event.currentTarget.id.split("_");
      this.newInvoiceModal.id = null;
      this.newInvoiceModal.encounterIds = [encounter];
      this.newInvoiceModal.episodeOfCareId = eoc;
      this.newInvoiceModal.show = true;
    },
    exportReport() {
      this.show();
      reportServer
        .downloadCustomReport(this.systemSetting.reportUrl, "Billings", {
          eocIds: JSON.stringify(this.filter.eocId.map(x => x.value)),
          providerIds: JSON.stringify(this.filter.providerId.map(x => x.value)),
          locationIds: JSON.stringify(this.filter.locations.map(x => x.value)),
          statuses: JSON.stringify(
            this.filter.submittedStatus.map(x =>
              x.value == "Submitted" ? true : false
            )
          ),
          dateFrom: this.filter.dateFrom
            ? formatStartDateSql(this.filter.dateFrom)
            : null,
          dateTo: this.filter.dateTo
            ? formatEndDateSql(this.filter.dateTo)
            : null,
          assignedToMe: this.assignedToMe
        })
        .then(res => {
          res.blob().then(blob => {
            let fileURL = URL.createObjectURL(blob);
            var link = document.createElement("a");
            link.href = fileURL;
            link.download = "Billings";
            link.dispatchEvent(new MouseEvent("click"));
          });
          this.hide();
        });
    },
    changeAssignedToMEToggle() {
      this.toggleAssignToMeBillingIndex();
      this.fetchBillings(true);
    },
    async onModifySubmitUb04Charge() {
      this.ubChargeModal.show = false;
      this.fetchInitialData();
    },
    async createNewInvoice(data) {
      await this.onSave(data);
    },
    async onSave(record) {
      this.newInvoiceModal = {
        show: true,
        id: null,
        episodeOfCareId: record.eocId,
        encounterIds: [record.id]
      };
    },
    addPaymentFromInvoiceView(data) {
      this.newPaymentModal.id = data.id;
      this.newPaymentModal.patientId = data.episodeOfCare.patientId;
      this.newPaymentModal.patientName = data.episodeOfCare.patient.name;
      this.newPaymentModal.show = true;
      this.invoiceViewModal.show = false;
    }
  },
  computed: {
    ...mapState("layout", {
      isPracticeSuiteActive: state => state.isPracticeSuiteActive,
      isCollaborateMDActive: state => state.isCollaborateMDActive,
      currentUser: state => state.currentUser,
      systemSetting: state => state.systemSetting,
      assignedToMe: state => state.assignedToMeBillingIndex
    }),
    ...mapState("client", {
      locations: state => state.locations
    }),
    ...mapGetters("layout", ["hasCurrentUserReportPermission"]),
    isSaisystemActive() {
      return this.systemSetting?.saisystemsEnabled;
    },
    hasFilters() {
      return (
        this.filter.eocId.length > 0 ||
        this.filter.submittedStatus.length > 0 ||
        this.filter.locations.length > 0 ||
        this.filter.providerId.length > 0 ||
        this.filter.dateFrom ||
        this.filter.dateTo ||
        this.filter.appointmentStatus.length > 0 ||
        this.filter.diagnosisCodes.length > 0 ||
        this.filter.placeOfServices.length > 0 ||
        this.filter.services.length > 0 ||
        this.filter.client.from ||
        this.filter.client.to ||
        this.filter.fee.from ||
        this.filter.fee.to ||
        this.filter.insuranceCompanies.length > 0 ||
        this.filter.insuranceFee.from ||
        this.filter.insuranceFee.to ||
        this.filter.univoicedAmount.from ||
        this.filter.univoicedAmount.to ||
        this.filter.documentationStatus.length > 0
      );
    },
    items() {
      return this.list.map(item => {
        const docHeight = 35;
        const margin = 10;
        const documentsCount = this.showAllInvoices[item.id]
          ? item.invoices.length
          : 3;
        const rowHeight = documentsCount * docHeight + margin * 2;

        const sum = xs => xs.reduce((a, x) => a + x, 0);
        const total = sum(
          item.services.map(x => x.costPerUnit * x.numberOfUnits)
        );
        const paid = sum(item.invoices.map(x => x.amountPaid));
        const unpaid = sum(
          item.invoices.map(x => x.totalAmount - x.amountPaid)
        );
        const insurance =
          total -
          (item.insurances?.length
            ? sum(item.insurances.map(x => x.totalAmount))
            : item.clientPaymentAmount);
        return {
          ...item,
          total: total,
          clientPaymentAmount: this.toCurrencyValue(item.clientPaymentAmount),
          insurance: this.toCurrencyValue(Math.max(0, insurance)),
          invoiceRefNumber:
            item.invoices != undefined ? item.invoices.refNumber : null,
          unpaid: item.invoices.length
            ? paid > 0
              ? `<div class="d-flex justify-content-start align-items-center w-100">
                  ${this.toCurrencyValue(paid)}
                   <span style="font-size:10px; padding: 2px 10px;" class="badge-v2 ml-2 badge-success">Paid</span>
                </div>`
              : ``
            : ``,
          paid: item.invoices.length
            ? unpaid > 0
              ? `<div class="d-flex justify-content-start align-items-center">
                    ${this.toCurrencyValue(unpaid)}
                    <span style="font-size:10px; padding: 2px 10px;" class="badge-v2 ml-2 badge-danger">Unpaid</span>
                </div>`
              : ``
            : ``,
          rowHeight: rowHeight
        };
      });
    },
    columns() {
      const self = this;
      return [
        {
          text: "Patient",
          field: "patient",
          type: "template",
          flex: 1,
          editor: false,
          template: ({ record }) => {
            const cssClass = record.documentationCompleted
              ? "text-success"
              : "text-danger";

            return (
              (record.data.mrnEocn
                ? `<i style="font-size: 13px;" class="fa fa-file mr-2 ${cssClass}"></i>` +
                  record.data.mrnEocn +
                  " "
                : "") + record.data.patient
            );
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Date & time",
          field: "dateAndTime",
          type: "template",
          width: 160,
          editor: false,
          template: data =>
            data.record.data.allDay
              ? formatDateOnly(data.record.data.date) + " All day"
              : data.record.data.date
              ? formatDate(data.record.data.date)
              : "",
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Services",
          field: "serviceCodes",
          type: "template",
          flex: 1,
          editor: false,
          template: data => {
            return data.record.data.services?.map(x => x.code).join(", ");
          },
          sortable(a, b) {
            const servicesA = a.services?.map(x => x.name).join(", ");
            const servicesB = b.services?.map(x => x.name).join(", ");
            return servicesA.localeCompare(servicesB);
          }
        },
        {
          text: "Appointment Status",
          field: "appointmentStatus",
          type: "template",
          width: 120,
          editor: false,
          template: data => {
            const status = data.record.data.appointmentStatus;
            const color = status.color;
            const bgColor = lightenDarkenColor(color, 85);

            return `<span class="badge-v2 py-2 badge-fixed-size" style="color: ${color}; background-color: ${bgColor}">${status.name}</span>`;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Diagnosis codes",
          field: "diagnosisCodes",
          type: "template",
          flex: 1,
          editor: false,
          template: data => {
            return data.record.data.diagnosisCodes?.map(x => x.code).join(", ");
          },
          sortable(a, b) {
            const diagnosisCodesA = a.diagnosisCodes
              ?.map(x => x.code)
              .join(", ");
            const diagnosisCodesB = b.diagnosisCodes
              ?.map(x => x.code)
              .join(", ");
            return diagnosisCodesA.localeCompare(diagnosisCodesB);
          }
        },
        {
          text: "Provider",
          field: "provider",
          type: "template",
          flex: 1,
          editor: false,
          template: data => data.record.data.provider,
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Place of service",
          field: "placeOfService",
          type: "template",
          flex: 1,
          editor: false,
          template: data =>
            data.record.data.placeOfService
              ? `${data.record.data.placeOfService?.code}-${data.record.data.placeOfService?.name}`
              : "",
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        this.systemSetting?.showInsuranceNameInEncounterIndex
          ? {
              text: "Insurance",
              field: "insuranceCompanies",
              type: "template",
              width: 220,
              editor: false,
              template: data =>
                  data.value?.length > 0
                      ? `<div class="text-wrap" style="min-height: 100px;
                                               height: 100%;
                                               padding: 20px 20px 20px 0px;
                                               display: flex;
                                               align-items: center;">
                  <div style="max-height: 100%;
                              overflow-y: auto;
                              padding-right: 10px;">` +
                      data.value.map(x => x.company.name).join(",") +
                      `</div>
                </div>`
                      : "",
              sortable() {
                return self.filter.orderByDescending ? -1 : 1;
              }
            }
          : {
              text: "Location",
              field: "location",
              type: "template",
              flex: 1,
              editor: false,
              template: data => data.record.data.location,
              sortable() {
                return self.filter.orderByDescending ? -1 : 1;
              }
            },
        {
          text: "Submission Status",
          field: "submissionStatus",
          type: "template",
          width: 120,
          editor: false,
          template: data => {
            const status = data.record.data.submissionStatus
              ? "Submitted"
              : "Not Submitted";
            let className = "warning";
            if (status == "Submitted") {
              className = "success";
            }
            return `<span class="badge-v2 py-2 badge-${className} badge-fixed-size">${status}</span>`;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Fee",
          field: "total",
          type: "template",
          width: 80,
          editor: false,
          template: data => {
            return this.toCurrencyValue(data.record.data.total);
          },
          sortable(a, b) {
            return a.total.localeCompare(b.total);
          }
        },
        {
          text: "Insurance",
          field: "total",
          type: "template",
          width: 100,
          editor: false,
          template: data => {
            return data.record.data.insurance;
          },
          sortable(a, b) {
            return a.total.localeCompare(b.total);
          }
        },
        {
          text: "Client",
          field: "total",
          type: "template",
          width: 150,
          editor: false,
          template: data => {
            return data.record.data.invoices.length
              ? `
            <div class="d-flex justify-content-center flex-column w-100 my-1">
              ${data.record.data.unpaid}
              ${data.record.data.paid}
            </div>
              `
              : data.record.data.clientPaymentAmount;
          },
          sortable(a, b) {
            return a.total.localeCompare(b.total);
          }
        },
        {
          text: "Invoice",
          field: "total",
          type: "template",
          width: 160,
          editor: false,
          template: data => {
            return data.record.data.invoices
              ? `<div class="w-100">${data.record.data.invoices
                  .slice(
                    0,
                    self.showAllInvoices[data.record.data.id]
                      ? data.record.data.invoices.length
                      : Math.min(data.record.data.invoices.length, 2)
                  )
                  .map(x => {
                    return `<div id="invoice_${x.id}" class="d-flex justify-content-center flex-column w-100 my-1">
                  <a
                    href="#"
                    >
                      Invoice #${x.refNumber}
                  </a>
                </div>`;
                  })
                  .join("\n")}
                ${
                  data.record.data.invoices.length > 2 &&
                  !self.showAllInvoices[data.record.data.id]
                    ? `<div class="more-invoices px-2 py-1 d-flex w-100 justify-content-start align-items-center text-primary cursor-pointer" data-id="${
                        data.record.data.id
                      }">+${data.record.data.invoices.length - 2} more...</div>`
                    : ""
                }
                ${
                  data.record.data.invoices.length > 2 &&
                  self.showAllInvoices[data.record.data.id]
                    ? `<div class="less-invoices px-2 py-1 d-flex w-100 justify-content-start align-items-center text-primary cursor-pointer" data-id="${data.record.data.id}">Less...</div>`
                    : ""
                }
                ${
                  data.record.data.uninvoicedAmount > 0
                    ? `<div class="d-flex justify-content-center flex-column w-100  my-1 text-red">Uninvoiced: ${this.toCurrencyValue(
                        data.record.data.uninvoicedAmount
                      )}</div>
                <div id="createInvoice_${data.record.data.id}_${
                        data.record.data.episodeOfCare.id
                      }" class="d-flex justify-content-center flex-column w-100  my-1"><a href="#"> + Create Invoice</a></div>`
                    : ""
                }
              </div>`
              : "";
          },
          sortable: false
        },
        {
          text: "",
          type: "action",
          align: "right",
          cellCls: "pointer-cell",
          width: 80,
          actions: [
            {
              tooltip: "View",
              renderer: () =>
                `<i class="fa fa-search-plus color-custom-dark-grey hover-text-primary b-action-item  grid-action-icon"></i>`,
              onClick: ({ record }) => {
                // show billing view modal
                self.billingViewModal.id = record.data.id;
                self.billingViewModal.show = true;
              }
            },
            {
              tooltip: "More",
              renderer: ({ record }) => {
                const actions = [];
                if (
                  self.isPracticeSuiteActive ||
                  self.isCollaborateMDActive ||
                  self.systemSetting.saisystemsEnabled
                ) {
                  actions.push({
                    content: "Submit insurance charge",
                    callback: async () => {
                      this.$dialog
                        .confirm(
                          {
                            title:
                              "Are you sure you want to submit this charge?"
                          },
                          { view: "confirm" }
                        )
                        .then(async () => {
                          const errors = validateEncountersForSubmit([
                            record.data
                          ]);
                          if (errors.length) {
                            this.showError({
                              message: "Errors: \r\n" + errors.join("\r\n"),
                              multiline: true
                            });
                            return;
                          }

                          let result = await billingServer.submit([
                            record.data.id
                          ]);
                          if (result.errorCode) {
                            this.$dialog.alert("Error: " + result.message);
                          } else {
                            this.fetchBillings(true);
                            this.showAlertSuccess();
                          }
                        })
                        .catch(function() {});
                    }
                  });

                  actions.push({
                    content: "Create UB04 Charge",
                    callback: () => {
                      this.ubChargeModal.encounterId = record.data.id;
                      this.ubChargeModal.show = true;
                    }
                  });
                }

                actions.push({
                  content: "Delete",
                  callback: () => {
                    this.$dialog
                      .alert(
                        {
                          title: "Are you sure you want to delete this record?"
                        },
                        {
                          view: "confirm-delete"
                        }
                      )
                      .then(async () => {
                        let result = null;
                        result = await billingServer.delete(record.data.id);
                        if (result.errorCode) {
                          this.$dialog.alert("Error: " + result.message);
                        } else {
                          this.fetchBillings(true);
                          this.showAlertSuccess();
                        }
                      });
                  }
                });

                return this.createDropdownButton({
                  items: actions,
                  menuWidth: 200
                });
              }
            }
          ]
        }
      ].filter(column => {
        if (this.showing && !this.systemSetting.enablePaymentSystem) {
          if (column.text === "Invoice") return false;
        }
        return true;
      });
    },
    exportFilter() {
      return {
        eocIds: this.filter.eocId.map(f => f.value),
        providerIds: this.filter.providerId.map(f => f.value),
        dateFrom: this.filter.dateFrom,
        dateTo: this.filter.dateTo,
        statusValues: this.filter.submittedStatus.map(s =>
          s.value == "Submitted" ? true : false
        ),
        orderBy: this.filter.orderBy,
        sort: this.filter.orderByDescending ? "DESC" : "ASC",
        locationIds: this.filter.locations.map(f => f.value),
        servicesid: this.filter.services.map(service => service.value),
        appointmentStatus: this.filter.appointmentStatus.map(
          appointmentStatus => appointmentStatus.value
        ),
        diagnosisCodeIds: this.filter.diagnosisCodes.map(
          icdCode => icdCode.value
        ),
        placeOfServiceIds: this.filter.placeOfServices.map(pos => pos.value),
        feeFrom: this.filter.fee.from,
        feeTo: this.filter.fee.to,
        insuranceFrom: this.filter.insuranceFee.from,
        insuranceTo: this.filter.insuranceFee.to,
        clientFrom: this.filter.client.from,
        clientTo: this.filter.client.to,
        uninvoicedAmountFrom: this.filter.univoicedAmount.from,
        uninvoicedAmountTo: this.filter.univoicedAmount.to,
        documentationStatus: this.filter.documentationStatus.map(s => s.value),
        searchPhrase:
          this.filter.searchPhrase.length >= 3 ? this.filter.searchPhrase : "",
        assignedToMe: this.assignedToMe
      };
    }
  },
  watch: {
    "filter.submittedStatus": function() {
      this.changePage(0);
      this.fetchBillings();
    },
    "filter.eocId": function() {
      this.changePage(0);
      this.fetchBillings();
    },
    "filter.providerId": function() {
      this.changePage(0);
      this.fetchBillings();
    },
    "filter.dateFrom": function() {
      this.changePage(0);
      this.fetchBillings();
    },
    "filter.dateTo": function() {
      this.changePage(0);
      this.fetchBillings();
    },
    showing: {
      handler: async function(newValue) {
        if (newValue) {
          await this.fetchInitialData;
        }
        if (newValue && this.refetchBillings) {
          this.fetchInitialData(true);
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.filter-select {
  height: 50px;
  width: 300px;
  padding-top: 5px;
}

.clients-list {
  height: calc(100vh - 240px - var(--filter-height));
  overflow-y: auto;
}

.clients-list >>> .b-grid-container .b-container {
  max-height: calc(100vh - 240px - var(--filter-height)) !important;
}

.border-yellow {
  border-color: #ffc107 !important;
}

.separator-or {
  margin-bottom: -13px;
}

.dg-main-content {
  max-width: 550px;
}
</style>

<style>
.filter-select .vs__dropdown-toggle {
  height: 100%;
  border-radius: 0;
  border: 0;
}

.patient-picker-hide-options .vs__dropdown-menu {
  display: none;
}

.patient-picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}
</style>
